import Vue from "vue";
import type { CreateElement, RenderContext } from "vue";

const DURATION_MS = 300;

// CSS alone cannot animate to an intrinsic size (max-height: 0 → fit-content
// snaps instead of sliding), so this transition measures the content and
// animates max-height to that exact pixel value. Overflow is clipped only
// while animating — the settled elements rely on visible overflow for their
// absolutely-positioned popups — and the inline styles use !important so they
// win over the `.overflow-visible` utility class.
function animate(el: HTMLElement, from: string, to: string, done: () => void) {
	let finished = false;
	const finish = () => {
		if (finished) return;
		finished = true;
		el.removeEventListener("transitionend", onEnd);
		el.style.transition = "";
		el.style.removeProperty("max-height");
		el.style.removeProperty("overflow");
		done();
	};
	const onEnd = (e: TransitionEvent) => {
		if (e.target === el && e.propertyName === "max-height") finish();
	};

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		finish();
		return;
	}

	el.style.setProperty("overflow", "hidden", "important");
	el.style.setProperty("max-height", from, "important");
	void el.offsetHeight; // flush layout so the transition starts from `from`
	el.style.transition = `max-height ${DURATION_MS}ms ease-in-out`;
	el.style.setProperty("max-height", to, "important");
	el.addEventListener("transitionend", onEnd);
	window.setTimeout(finish, DURATION_MS + 100); // safety net if transitionend never fires
}

export default Vue.extend({
	name: "ExpandTransition",
	functional: true,
	render(h: CreateElement, ctx: RenderContext) {
		return h(
			"transition",
			{
				props: { css: false },
				on: {
					enter(el: HTMLElement, done: () => void) {
						animate(el, "0px", `${el.scrollHeight}px`, done);
					},
					leave(el: HTMLElement, done: () => void) {
						animate(el, `${el.scrollHeight}px`, "0px", done);
					},
				},
			},
			ctx.children
		);
	},
});
